import { useCallback, useMemo, useState } from 'react';
import { useMatch, useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { ROUTES } from '../../../constants/RouteNameMapping';
import { ScheduleValue, SourcingList, SourcingSavedSearch } from '../../../schemas/SourcingList.schema';
import { IEnrichCompanyRequest, ISourcingCompanyRow } from '../../../schemas/SourcingListItems.schema';
import {
  createSourcingList,
  deleteSourcingList,
  duplicateSourcingList,
  enrichCompanies,
  fetchListCompanies,
  refreshListNow,
  renameSourcingList,
  updateListSchedule,
} from '../../../services/queries/SourcingListsQueries';
import {
  SOURCING_LIST_COMPANIES_KEY,
  SOURCING_LIST_PEOPLE_KEY,
  SOURCING_LISTS_KEY,
  useSourcingListsQuery,
} from '../../../services/queries-hooks/SourcingListsQueriesHooks';

const FIVE_MINUTES = 5 * 60 * 1000;

export function useCreateSourcingList() {
  const queryClient = useQueryClient();
  const { pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: ({ name, entityType }: { name: string; entityType: 'company' | 'person' }) =>
      createSourcingList(name, entityType),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [SOURCING_LISTS_KEY] });
    },
    onError: (err, vars) => {
      if (isListNameTakenError(err)) {
        pushErrorToast({ message: `A list named "${vars.name}" already exists.` });
        return;
      }
      pushErrorToast({ message: 'Failed to create list. Please try again.' });
    },
  });
}

export function useDeleteSourcingList() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const currentListMatch = useMatch(`/${ROUTES.SOURCING}/${ROUTES.LISTS}/:listId`);
  const { pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: (listId: string) => deleteSourcingList(listId),
    onSuccess: async (_data, listId) => {
      await queryClient.invalidateQueries({ queryKey: [SOURCING_LISTS_KEY] });
      if (currentListMatch?.params.listId === listId) {
        navigate(`/${ROUTES.SOURCING}`);
      }
    },
    onError: () => {
      pushErrorToast({ message: 'Failed to delete list. Please try again.' });
    },
  });
}

export function useRenameSourcingList() {
  const queryClient = useQueryClient();
  const { pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameSourcingList(id, name),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [SOURCING_LISTS_KEY] });
    },
    onError: (err, vars) => {
      if (isListNameTakenError(err)) {
        pushErrorToast({ message: `A list named "${vars.name}" already exists.` });
        return;
      }
      pushErrorToast({ message: 'Failed to rename list. Please try again.' });
    },
  });
}

export function useDuplicateSourcingList() {
  const queryClient = useQueryClient();
  const { pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: ({ listId, shortName }: { listId: string; shortName?: string }) =>
      duplicateSourcingList(listId, shortName),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [SOURCING_LISTS_KEY] });
    },
    onError: (err, vars) => {
      if (isListNameTakenError(err) && vars.shortName) {
        pushErrorToast({ message: `A list named "${vars.shortName}" already exists.` });
        return;
      }
      pushErrorToast({ message: 'Failed to duplicate list. Please try again.' });
    },
  });
}

/**
 * Detect a name-conflict response on a list mutation. The backend's canonical
 * signal is `data.code === 'LIST_NAME_TAKEN'`; we check that first so any
 * future 409 the BE might add for a different reason (e.g. concurrent edit)
 * doesn't get mislabeled as a name conflict. Status `409` is a fallback for
 * the rename/create endpoints that historically don't always set the `code`.
 */
function isListNameTakenError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) {
    return false;
  }
  const response = (err as { response?: { status?: number; data?: { code?: string } } }).response;
  if (!response) {
    return false;
  }
  if (response.data?.code === 'LIST_NAME_TAKEN') {
    return true;
  }
  return response.status === 409;
}

/**
 * Bundles open/close state for the duplicate-rename dialog with the mutation
 * so both invocation sites (page-level Duplicate menu and sidebar list item)
 * share the exact same flow. Pulls the loaded sourcing lists to enable a
 * client-side collision check; the server's 409 LIST_NAME_TAKEN is still the
 * authority, so we also surface it inline if the cached list lagged a write.
 */
export function useDuplicateListDialog(listId: string | undefined) {
  const [open, setOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { pushErrorToast } = useToastMessageState();
  const { data: sourcingData } = useSourcingListsQuery();

  const existingNames = useMemo(() => (sourcingData?.lists ?? []).map((l) => l.name), [sourcingData?.lists]);

  const { isPending, mutate } = useMutation({
    mutationFn: ({ id, shortName }: { id: string; shortName: string }) =>
      duplicateSourcingList(id, shortName),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [SOURCING_LISTS_KEY] });
      setServerError(null);
      setOpen(false);
    },
    onError: (err, vars) => {
      if (isListNameTakenError(err)) {
        setServerError(`A list named "${vars.shortName}" already exists.`);
        return;
      }
      pushErrorToast({ message: 'Failed to duplicate list. Please try again.' });
    },
  });

  const openDialog = useCallback(() => {
    setServerError(null);
    setOpen(true);
  }, []);
  const closeDialog = useCallback(() => {
    setServerError(null);
    setOpen(false);
  }, []);
  const confirm = useCallback(
    (shortName: string) => {
      if (!listId) {
        return;
      }
      setServerError(null);
      mutate({ id: listId, shortName });
    },
    [listId, mutate]
  );

  return { open, isPending, existingNames, serverError, openDialog, closeDialog, confirm };
}

export function useUpdateListSchedule() {
  const queryClient = useQueryClient();
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: ({ list, schedule }: { list: SourcingList | SourcingSavedSearch; schedule: ScheduleValue }) =>
      updateListSchedule(list, schedule),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [SOURCING_LISTS_KEY] });
      pushSuccessToast({ message: 'Refresh settings saved.' });
    },
    onError: () => {
      pushErrorToast({ message: 'Failed to save refresh settings. Please try again.' });
    },
  });
}

export function useRefreshListNow() {
  const queryClient = useQueryClient();
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: (savedSearch: SourcingSavedSearch) => refreshListNow(savedSearch.id),
    onSuccess: async (result, savedSearch) => {
      const itemsKey =
        savedSearch.entityType === 'company'
          ? [SOURCING_LIST_COMPANIES_KEY, savedSearch.id, 'company']
          : [SOURCING_LIST_PEOPLE_KEY, savedSearch.id, 'person'];
      await queryClient.invalidateQueries({ queryKey: [SOURCING_LISTS_KEY] });
      await queryClient.invalidateQueries({ queryKey: itemsKey });
      pushSuccessToast({
        message: `Refreshed: ${result.added} added, ${result.unchanged} unchanged.`,
      });
    },
    onError: () => {
      pushErrorToast({ message: 'Failed to refresh list. Please try again.' });
    },
  });
}

export function useEnrichListNow() {
  const queryClient = useQueryClient();
  const { pushSuccessToast, pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: async (list: SourcingList) => {
      if (list.entityType !== 'company') {
        throw new Error('Person enrichment is not yet supported.');
      }
      const items = await queryClient.fetchQuery<ISourcingCompanyRow[]>({
        queryKey: [SOURCING_LIST_COMPANIES_KEY, list.id, 'company'],
        queryFn: ({ signal }) => fetchListCompanies(list.id, signal),
        staleTime: FIVE_MINUTES,
      });
      if (items.length === 0) {
        throw new Error('No entities to enrich.');
      }
      const payload: IEnrichCompanyRequest[] = items.map((c) => ({
        name: c.name,
        domain: c.domain ?? undefined,
        linkedinUrl: c.linkedinUrl ?? undefined,
      }));
      return enrichCompanies(payload);
    },
    onSuccess: async (res, list) => {
      await queryClient.invalidateQueries({
        queryKey: [SOURCING_LIST_COMPANIES_KEY, list.id, 'company'],
      });
      pushSuccessToast({ message: `Enrichment queued for ${res.entityCount} entities` });
    },
    onError: (err) => {
      const message =
        err instanceof Error && err.message ? err.message : 'Failed to enrich. Please try again.';
      pushErrorToast({ message });
    },
  });
}
